import { Trans, useLingui } from "@lingui/react/macro";
import type {
  DrokMcpAuthMode,
  DrokTemplatePolicy,
  DrokToolGroupName,
  DrokToolkit,
} from "@rakazo/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Spinner,
  Textarea,
  toast,
} from "@rakazo/ui-web";
import { NativeSelect, NativeSelectOption } from "@rakazo/ui-web/components/ui/native-select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";
import { CheckRow } from "./check-row";
import { ToolGroupFields } from "./policy-fields";
import { ToolkitPicker } from "./toolkit-picker";

/** The deployment's one model provider; a source agent on anything else cannot be published. */
export const DROK_COMPANY_PROVIDER = "drok-company";

export type PublishTarget =
  | { kind: "new" }
  | { kind: "version"; templateId: string; name: string; sourceBotId: string | null };

interface PublishDialogProps {
  target: PublishTarget | null;
  onClose: () => void;
  onPublished: (templateId: string) => void;
}

interface Picks {
  routineIds: string[];
  skillIds: string[];
  mcpServers: Array<{ serverId: string; authMode: DrokMcpAuthMode }>;
  toolGroups: DrokToolGroupName[];
  toolkits: DrokToolkit[];
}

const EMPTY: Picks = {
  routineIds: [],
  skillIds: [],
  mcpServers: [],
  toolGroups: [],
  toolkits: [],
};

function toggle(list: string[], id: string, on: boolean): string[] {
  return on ? [...list, id] : list.filter((item) => item !== id);
}

function Warnings({ warnings }: { warnings: Array<{ code: string; message: string }> }) {
  if (warnings.length === 0) return null;
  return (
    <div
      role="status"
      className="rounded-xl bg-muted/50 p-3 text-sm"
      data-testid="publish-warnings"
    >
      <p className="font-medium">
        <Trans>Copies will not carry these:</Trans>
      </p>
      <ul className="ms-4 list-disc">
        {warnings.map((warning) => (
          <li key={warning.code}>{warning.message}</li>
        ))}
      </ul>
    </div>
  );
}

/** Publishes an agent of IT's as a new template, or a new version of an existing one. */
export function PublishDialog({ target, onClose, onPublished }: PublishDialogProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const open = target !== null;
  const isVersion = target?.kind === "version";
  const [botId, setBotId] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [picks, setPicks] = useState<Picks>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const bots = useQuery({
    queryKey: ["drok", "admin.sourceBots"],
    queryFn: () => drokRpc.templates.sourceBots(),
    enabled: open,
  });
  const templateId = target?.kind === "version" ? target.templateId : null;
  const previous = useQuery({
    queryKey: ["drok", "admin.publishPreselect", templateId],
    queryFn: async () => {
      const detail = await drokRpc.templates.get({ templateId: templateId ?? "" });
      const latest = detail.versions[0];
      if (!latest) return null;
      const version = await drokRpc.templates.getVersion({
        templateId: templateId ?? "",
        version: latest.version,
      });
      return { policy: version.policy, snapshot: version.snapshot };
    },
    enabled: templateId !== null,
  });
  const details = useQuery({
    queryKey: ["drok", "admin.sourceDetails", botId],
    queryFn: () => drokRpc.templates.sourceDetails({ botId }),
    enabled: open && botId !== "",
  });

  useEffect(() => {
    if (!target) return;
    setName("");
    setNotes("");
    setError(null);
    setPicks(EMPTY);
    setBotId(target.kind === "version" ? (target.sourceBotId ?? "") : "");
  }, [target]);

  // A new version starts from the last one's tool groups and apps.
  useEffect(() => {
    const policy: DrokTemplatePolicy | undefined = previous.data?.policy;
    if (policy) {
      setPicks((current) => ({
        ...current,
        toolGroups: policy.toolGroups,
        toolkits: policy.toolkits,
      }));
    }
  }, [previous.data]);

  // ...and carries the routines and skills it carried, matched by name.
  useEffect(() => {
    if (!details.data || !previous.data) return;
    const routineNames = new Set(previous.data.snapshot.routines.map((r) => r.name));
    const skillNames = new Set(previous.data.snapshot.skills.map((s) => s.name));
    const previousMcp = previous.data.snapshot.mcpServers ?? [];
    setPicks((current) => ({
      ...current,
      routineIds: details.data.routines
        .filter((r) => r.carryable && routineNames.has(r.name))
        .map((r) => r.id),
      skillIds: details.data.skills.filter((s) => skillNames.has(s.name)).map((s) => s.id),
      mcpServers: (details.data.mcpServers ?? []).flatMap((server) => {
        const previousServer = previousMcp.find((entry) => entry.sourceServerId === server.id);
        return server.enabled && previousServer
          ? [{ serverId: server.id, authMode: previousServer.authMode }]
          : [];
      }),
    }));
  }, [details.data, previous.data]);

  const bot = bots.data?.find((row) => row.id === botId);
  const modelRefused = bot !== undefined && bot.effectiveModelProvider !== DROK_COMPANY_PROVIDER;

  const publish = useMutation({
    mutationFn: async () => {
      const common = {
        routineIds: picks.routineIds,
        skillIds: picks.skillIds,
        mcpServers: picks.mcpServers,
        notes: notes.trim(),
        policy: { toolGroups: picks.toolGroups, toolkits: picks.toolkits },
      };
      if (target?.kind === "version") {
        return drokRpc.templates.publishVersion({
          ...common,
          templateId: target.templateId,
          botId,
        });
      }
      return drokRpc.templates.publish({ ...common, botId, name: name.trim() });
    },
    onSuccess: async (result) => {
      toast.success(t`Published version ${result.version}`);
      await queryClient.invalidateQueries({ queryKey: ["drok"] });
      onPublished(result.templateId);
      onClose();
    },
    onError: (e) => setError(errorText(e)),
  });

  const canPublish =
    botId !== "" &&
    !modelRefused &&
    !publish.isPending &&
    details.isSuccess &&
    (isVersion || name.trim() !== "");
  const close = () => {
    if (!publish.isPending) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isVersion ? <Trans>Publish a new version</Trans> : <Trans>Publish a template</Trans>}
          </DialogTitle>
          <DialogDescription>
            <Trans>Employees get a locked copy of the agent as it is now.</Trans>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isVersion ? null : (
            <Input
              aria-label={t`Template name`}
              placeholder={t`Template name`}
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
          {bots.isPending ? <Spinner aria-label={t`Loading…`} /> : null}
          {bots.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {errorText(bots.error)}
            </p>
          ) : null}
          {bots.data && bots.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              <Trans>You have no agents to publish. Create one first.</Trans>
            </p>
          ) : null}
          {bots.data && bots.data.length > 0 ? (
            <NativeSelect
              aria-label={t`Agent to publish`}
              className="w-full"
              value={botId}
              onChange={(e) => {
                setBotId(e.target.value);
                setPicks((current) => ({
                  ...current,
                  routineIds: [],
                  skillIds: [],
                  mcpServers: [],
                }));
              }}
            >
              <NativeSelectOption value="">{t`Choose an agent`}</NativeSelectOption>
              {bots.data.map((row) => (
                <NativeSelectOption key={row.id} value={row.id}>
                  {row.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          ) : null}
          {bot ? (
            <p className="text-sm text-muted-foreground" data-testid="publish-model">
              {bot.modelIsDefault ? (
                <Trans>Model: {bot.effectiveModelId} (company default)</Trans>
              ) : (
                <Trans>Model: {bot.effectiveModelId}</Trans>
              )}
            </p>
          ) : null}
          {modelRefused ? (
            <p
              role="alert"
              className="text-sm text-destructive"
              data-testid="publish-model-refused"
            >
              <Trans>
                This agent does not use a company model. Pick one in the agent's settings first.
              </Trans>
            </p>
          ) : null}
          {details.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {errorText(details.error)}
            </p>
          ) : null}
          {details.data ? (
            <>
              <fieldset className="space-y-1">
                <legend className="text-sm font-medium">
                  <Trans>Routines</Trans>
                </legend>
                {details.data.routines.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    <Trans>None</Trans>
                  </p>
                ) : null}
                {details.data.routines.map((routine) => (
                  <CheckRow
                    key={routine.id}
                    disabled={!routine.carryable}
                    checked={picks.routineIds.includes(routine.id)}
                    onChange={(on) =>
                      setPicks({ ...picks, routineIds: toggle(picks.routineIds, routine.id, on) })
                    }
                  >
                    <span>{routine.name}</span>
                    {routine.carryable ? null : (
                      <span className="text-xs text-muted-foreground">
                        <Trans>No schedule</Trans>
                      </span>
                    )}
                  </CheckRow>
                ))}
              </fieldset>
              <fieldset className="space-y-1">
                <legend className="text-sm font-medium">
                  <Trans>Skills</Trans>
                </legend>
                {details.data.skills.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    <Trans>None</Trans>
                  </p>
                ) : null}
                {details.data.skills.map((skill) => (
                  <CheckRow
                    key={skill.id}
                    checked={picks.skillIds.includes(skill.id)}
                    onChange={(on) =>
                      setPicks({ ...picks, skillIds: toggle(picks.skillIds, skill.id, on) })
                    }
                  >
                    <span>{skill.name}</span>
                  </CheckRow>
                ))}
              </fieldset>
              {(details.data.mcpServers ?? []).length > 0 ? (
                <fieldset className="space-y-2" data-testid="publish-mcp-servers">
                  <legend className="text-sm font-medium">
                    <Trans>MCP connections</Trans>
                  </legend>
                  {details.data.mcpServers.map((server) => {
                    const selected = picks.mcpServers.find((pick) => pick.serverId === server.id);
                    return (
                      <div key={server.id} className="space-y-2">
                        <CheckRow
                          disabled={!server.enabled}
                          checked={selected !== undefined}
                          onChange={(on) =>
                            setPicks((current) => ({
                              ...current,
                              mcpServers: on
                                ? [
                                    ...current.mcpServers,
                                    { serverId: server.id, authMode: "employee" },
                                  ]
                                : current.mcpServers.filter((pick) => pick.serverId !== server.id),
                            }))
                          }
                        >
                          <span>{server.name}</span>
                          {!server.enabled ? (
                            <span className="text-xs text-muted-foreground">
                              <Trans>Disabled</Trans>
                            </span>
                          ) : null}
                        </CheckRow>
                        {selected ? (
                          <NativeSelect
                            aria-label={t`Credentials for ${server.name}`}
                            className="ms-6 w-auto"
                            value={selected.authMode}
                            onChange={(event) =>
                              setPicks((current) => ({
                                ...current,
                                mcpServers: current.mcpServers.map((pick) =>
                                  pick.serverId === server.id
                                    ? { ...pick, authMode: event.target.value as DrokMcpAuthMode }
                                    : pick,
                                ),
                              }))
                            }
                          >
                            <NativeSelectOption value="employee">
                              {t`Employee credentials`}
                            </NativeSelectOption>
                            <NativeSelectOption value="shared">
                              {t`Company credentials`}
                            </NativeSelectOption>
                          </NativeSelect>
                        ) : null}
                      </div>
                    );
                  })}
                </fieldset>
              ) : null}
              <Warnings warnings={details.data.warnings} />
            </>
          ) : null}
          <div className="space-y-2">
            <p className="text-sm font-medium">
              <Trans>Tools</Trans>
            </p>
            <ToolGroupFields
              value={picks.toolGroups}
              onChange={(toolGroups) => setPicks({ ...picks, toolGroups })}
            />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">
              <Trans>Apps employees can connect</Trans>
            </p>
            <ToolkitPicker
              value={picks.toolkits}
              onChange={(toolkits) => setPicks({ ...picks, toolkits })}
            />
          </div>
          <Textarea
            aria-label={t`Version notes`}
            placeholder={t`Version notes (optional)`}
            maxLength={4000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {error ? (
            <p role="alert" className="text-sm text-destructive" data-testid="publish-error">
              {error}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={publish.isPending} onClick={close}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            disabled={!canPublish}
            onClick={() => {
              setError(null);
              publish.mutate();
            }}
          >
            {publish.isPending ? <Spinner aria-label={t`Working…`} /> : null}
            <Trans>Publish</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
